import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { ReferralActivityRow } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface Contact {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}
interface Partner {
  id: number;
  name: string;
  region: string | null;
  contacts: Contact[];
  website: string | null;
  relationship_type: string | null;
  notes: string | null;
  last_contacted_at: string | null;
  active: boolean;
}

function fmtDate(ts: string | null) {
  if (!ts) return 'never';
  return new Date(ts).toLocaleDateString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function tone(rel: string | null): string {
  const r = (rel || '').toLowerCase();
  if (r.includes('inactive')) return 'var(--color-text-muted, #888)';
  if (r.includes('active')) return 'var(--color-teal, #00B097)';
  return 'var(--color-gold, #FEC553)';
}

export function Referrals() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['referral-partners'],
    queryFn: () => api.getReferralPartners(),
  });
  const logContact = useMutation({
    mutationFn: (id: number) => api.logReferralContact(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral-partners'] }),
  });

  // Referral activity: which partners are referring work (from mapped quotes).
  const { data: activityData } = useQuery({
    queryKey: ['referral-activity'],
    queryFn: () => api.getReferralActivity(90),
  });
  const activity: ReferralActivityRow[] = activityData?.rows || [];
  const activityById = new Map(activity.map((a) => [a.id, a]));

  // One-off backfill: map historical quotes to their referral partner. Defaults
  // to since 1 July 2026 (start of the current tracking period).
  const [since, setSince] = useState('2026-07-01');
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);
  const backfill = useMutation({
    mutationFn: () => api.backfillReferralActivity(since || null),
    onSuccess: (r) => {
      setBackfillMsg(
        `Mapped ${r.matched} quote${r.matched === 1 ? '' : 's'} to referrals ` +
          `(${r.scanned} scanned, ${r.alreadyLinked} already linked, ${r.ambiguous} ambiguous, ${r.unmatched} no match).`,
      );
      qc.invalidateQueries({ queryKey: ['referral-activity'] });
      qc.invalidateQueries({ queryKey: ['referral-partners'] });
    },
    onError: (e) => setBackfillMsg((e as Error)?.message || 'Backfill failed.'),
  });

  const activeReferrers = activity.filter((a) => a.quotes > 0 && !a.dormant).length;
  const dormantCount = activity.filter((a) => a.dormant).length;

  // Add-partner form
  const emptyForm = { name: '', region: '', relationship_type: 'Active', website: '', notes: '', cName: '', cPhone: '', cEmail: '' };
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const setF = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const createMut = useMutation({
    mutationFn: () =>
      api.createReferralPartner({
        name: form.name.trim(),
        region: form.region.trim() || null,
        relationship_type: form.relationship_type || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
        contacts: form.cName.trim() || form.cPhone.trim() || form.cEmail.trim()
          ? [{ name: form.cName.trim() || null, phone: form.cPhone.trim() || null, email: form.cEmail.trim() || null }]
          : [],
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['referral-partners'] }); setShowAdd(false); setForm(emptyForm); },
  });

  // Edit / delete a partner (manage its details and its contacts).
  const [editing, setEditing] = useState<Partner | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Partner | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteReferralPartner(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['referral-partners'] }); setConfirmDelete(null); },
  });

  const partners: Partner[] = data || [];
  const term = q.trim().toLowerCase();
  const shown = term
    ? partners.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          (p.region || '').toLowerCase().includes(term) ||
          (p.notes || '').toLowerCase().includes(term) ||
          p.contacts.some((c) => (c.name || '').toLowerCase().includes(term)),
      )
    : partners;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 'var(--spacing-3, 16px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>
          Referral partners{' '}
          <span style={{ fontSize: 14, color: 'var(--color-text-muted,#666)', fontWeight: 400 }}>
            ({partners.length})
          </span>
        </h2>
        <button
          onClick={() => setShowAdd(true)}
          style={{ background: 'var(--color-teal,#00B097)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer' }}
        >
          + Add partner
        </button>
      </div>
      <p style={{ color: 'var(--color-text-muted,#666)', marginTop: 0 }}>
        Agencies, agents and trades that send us work. Log a contact whenever you reach out so the
        nurture campaign knows who's gone quiet.
      </p>

      {/* ── Referral activity report ─────────────────────────────────── */}
      <div style={{ border: '1px solid var(--color-border,#e0e0e0)', borderRadius: 10, padding: 'var(--spacing-3,16px)', marginBottom: 20, background: 'var(--color-surface,#fff)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 2px' }}>Referral activity</h3>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted,#666)' }}>
              {activeReferrers} referring recently · <span style={{ color: 'var(--color-gold,#B8860B)' }}>{dormantCount} dormant</span> (quiet 90+ days or never)
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>Map quotes since</label>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid var(--color-border,#ccc)', borderRadius: 8 }} />
            <button onClick={() => { setBackfillMsg(null); backfill.mutate(); }} disabled={backfill.isPending}
              style={{ background: 'var(--color-navy,#1B365D)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer' }}>
              {backfill.isPending ? 'Mapping…' : 'Map quotes'}
            </button>
          </div>
        </div>
        {backfillMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--color-navy,#1B365D)', background: 'var(--color-teal-25,#D6EBE5)', borderRadius: 8, padding: '8px 10px' }}>
            {backfillMsg}
          </div>
        )}
        {activity.length > 0 && (
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--color-text-muted,#666)', borderBottom: '1px solid var(--color-border,#e0e0e0)' }}>
                  <th style={{ padding: '6px 8px' }}>Partner</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Quotes</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Accepted</th>
                  <th style={{ padding: '6px 8px' }}>Last referral</th>
                  <th style={{ padding: '6px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--color-border,#f0f0f0)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{a.name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{a.quotes}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{a.accepted}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--color-text-muted,#666)' }}>
                      {a.lastReferral ? fmtDate(a.lastReferral) : 'never'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {a.dormant ? (
                        <span style={{ color: 'var(--color-gold,#B8860B)', fontWeight: 600 }}>Dormant</span>
                      ) : (
                        <span style={{ color: 'var(--color-teal,#00B097)', fontWeight: 600 }}>Referring</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <input
        placeholder="Search name, region, contact, notes…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid var(--color-border,#ccc)',
          borderRadius: 8,
          marginBottom: 16,
        }}
      />

      {isLoading && <p>Loading…</p>}
      {!isLoading && shown.length === 0 && (
        <p style={{ color: 'var(--color-text-muted,#666)' }}>No partners match.</p>
      )}

      {shown.map((p) => (
        <div
          key={p.id}
          style={{
            border: '1px solid var(--color-border,#e0e0e0)',
            borderLeft: `4px solid ${tone(p.relationship_type)}`,
            borderRadius: 8,
            padding: 'var(--spacing-3,16px)',
            marginBottom: 12,
            background: 'var(--color-surface,#fff)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {p.name}
                {p.region && (
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted,#666)' }}> · {p.region}</span>
                )}
              </div>
              {p.relationship_type && (
                <div style={{ fontSize: 12, color: tone(p.relationship_type), marginTop: 2 }}>
                  {p.relationship_type}
                </div>
              )}
              {(() => {
                const a = activityById.get(p.id);
                if (!a) return null;
                return (
                  <div style={{ fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--color-text-muted,#666)' }}>
                      {a.quotes} referral{a.quotes === 1 ? '' : 's'}
                      {a.quotes > 0 && ` · last ${fmtDate(a.lastReferral)}`}
                    </span>
                    {a.dormant && (
                      <span style={{ background: 'var(--color-gold,#FEC553)', color: 'var(--color-dark-green,#073A3D)', borderRadius: 6, padding: '1px 7px', fontWeight: 700 }}>
                        Dormant
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ textAlign: 'right', minWidth: 150 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>
                last contacted: {fmtDate(p.last_contacted_at)}
              </div>
              <button
                onClick={() => logContact.mutate(p.id)}
                disabled={logContact.isPending}
                style={{
                  marginTop: 6,
                  background: 'var(--color-teal,#00B097)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Log contact
              </button>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                <button
                  onClick={() => setEditing(p)}
                  style={{ background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => setConfirmDelete(p)}
                  style={{ background: 'none', border: '1px solid var(--color-error,#d33)', color: 'var(--color-error,#d33)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          {p.contacts.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {p.contacts.map((c, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 13,
                    border: '1px solid var(--color-border,#eee)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    background: 'var(--color-surface-elevated,#fafafa)',
                  }}
                >
                  {c.name && <div style={{ fontWeight: 600 }}>{c.name}</div>}
                  {c.phone && <div>{c.phone}</div>}
                  {c.email && <div style={{ color: 'var(--color-navy,#1B365D)' }}>{c.email}</div>}
                </div>
              ))}
            </div>
          )}

          {p.notes && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-muted,#555)' }}>{p.notes}</div>
          )}
          {p.website && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer">
                {p.website}
              </a>
            </div>
          )}
        </div>
      ))}

      {showAdd && (() => {
        const field: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, boxSizing: 'border-box' };
        const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted,#666)', margin: '10px 0 4px' };
        return (
          <div role="dialog" aria-modal="true" onClick={() => setShowAdd(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--color-surface,#fff)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 520 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>New referral partner</h3>
                <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-muted,#666)' }}>✕</button>
              </div>

              <label style={label}>Name *</label>
              <input style={field} value={form.name} onChange={(e) => setF({ name: e.target.value })} placeholder="e.g. Ray White Bardon" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={label}>Region</label><input style={field} value={form.region} onChange={(e) => setF({ region: e.target.value })} placeholder="e.g. Bardon" /></div>
                <div><label style={label}>Relationship</label>
                  <select style={field} value={form.relationship_type} onChange={(e) => setF({ relationship_type: e.target.value })}>
                    {['Active', 'Intermittent', 'Inactive'].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <label style={label}>Website</label>
              <input style={field} value={form.website} onChange={(e) => setF({ website: e.target.value })} placeholder="raywhite.com" />

              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted,#666)' }}>Primary contact (optional)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={label}>Contact name</label><input style={field} value={form.cName} onChange={(e) => setF({ cName: e.target.value })} /></div>
                <div><label style={label}>Phone</label><input style={field} value={form.cPhone} onChange={(e) => setF({ cPhone: e.target.value })} /></div>
              </div>
              <label style={label}>Contact email</label>
              <input style={field} type="email" value={form.cEmail} onChange={(e) => setF({ cEmail: e.target.value })} />

              <label style={label}>Notes</label>
              <textarea style={{ ...field, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={(e) => setF({ notes: e.target.value })} />

              {createMut.isError && <p style={{ color: 'var(--color-error,#d33)', fontSize: 13 }}>{(createMut.error as Error)?.message || 'Could not add the partner.'}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={() => createMut.mutate()}
                  disabled={!form.name.trim() || createMut.isPending}
                  style={{ background: 'var(--color-teal,#00B097)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: form.name.trim() ? 'pointer' : 'not-allowed', opacity: form.name.trim() ? 1 : 0.5 }}
                >
                  {createMut.isPending ? 'Adding…' : 'Add partner'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {editing && (
        <EditPartnerModal
          key={editing.id}
          partner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['referral-partners'] }); setEditing(null); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={`Delete ${confirmDelete?.name ?? 'this partner'}?`}
        message="This removes the referral partner and all its contacts, and unlinks it from any quotes that used it as the referral agent. This can't be undone."
        tone="danger"
        confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete partner'}
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

/** Edit a partner's details and manage its contacts (add / edit / remove each). */
function EditPartnerModal({ partner, onClose, onSaved }: { partner: Partner; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(partner.name);
  const [region, setRegion] = useState(partner.region ?? '');
  const [relationship, setRelationship] = useState(partner.relationship_type ?? 'Active');
  const [website, setWebsite] = useState(partner.website ?? '');
  const [notes, setNotes] = useState(partner.notes ?? '');
  const [active, setActive] = useState(partner.active);
  const [contacts, setContacts] = useState<Contact[]>(
    (partner.contacts ?? []).map((c) => ({ name: c.name ?? '', phone: c.phone ?? '', email: c.email ?? '' })),
  );

  const saveMut = useMutation({
    mutationFn: () =>
      api.updateReferralPartner(partner.id, {
        name: name.trim(),
        region: region.trim() || null,
        relationship_type: relationship || null,
        website: website.trim() || null,
        notes: notes.trim() || null,
        active,
        // Drop rows with nothing in them; keep only the filled fields.
        contacts: contacts
          .map((c) => ({ name: (c.name || '').trim() || null, phone: (c.phone || '').trim() || null, email: (c.email || '').trim() || null }))
          .filter((c) => c.name || c.phone || c.email),
      }),
    onSuccess: onSaved,
  });

  const setContact = (i: number, patch: Partial<Contact>) =>
    setContacts((prev) => prev.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const field: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, boxSizing: 'border-box' };
  const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted,#666)', margin: '10px 0 4px' };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--color-surface,#fff)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Edit partner</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-muted,#666)' }}>✕</button>
        </div>

        <label style={label}>Name *</label>
        <input style={field} value={name} onChange={(e) => setName(e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={label}>Region</label><input style={field} value={region} onChange={(e) => setRegion(e.target.value)} /></div>
          <div><label style={label}>Relationship</label>
            <select style={field} value={relationship} onChange={(e) => setRelationship(e.target.value)}>
              {['Active', 'Intermittent', 'Inactive'].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <label style={label}>Website</label>
        <input style={field} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="raywhite.com" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 4px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted,#666)' }}>Contacts</div>
          <button
            onClick={() => setContacts((prev) => [...prev, { name: '', phone: '', email: '' }])}
            style={{ background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            + Add contact
          </button>
        </div>
        {contacts.length === 0 && <div style={{ fontSize: 13, color: 'var(--color-text-muted,#888)' }}>No contacts yet.</div>}
        {contacts.map((c, i) => (
          <div key={i} style={{ border: '1px solid var(--color-border,#eee)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--color-surface-elevated,#fafafa)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted,#666)' }}>Contact {i + 1}</span>
              <button
                onClick={() => setContacts((prev) => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', color: 'var(--color-error,#d33)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
              <input style={field} placeholder="Name" value={c.name ?? ''} onChange={(e) => setContact(i, { name: e.target.value })} />
              <input style={field} placeholder="Phone" value={c.phone ?? ''} onChange={(e) => setContact(i, { phone: e.target.value })} />
            </div>
            <input style={{ ...field, marginTop: 8 }} type="email" placeholder="Email" value={c.email ?? ''} onChange={(e) => setContact(i, { email: e.target.value })} />
          </div>
        ))}

        <label style={label}>Notes</label>
        <textarea style={{ ...field, minHeight: 60, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active partner
        </label>

        {saveMut.isError && <p style={{ color: 'var(--color-error,#d33)', fontSize: 13 }}>{(saveMut.error as Error)?.message || 'Could not save the partner.'}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={!name.trim() || saveMut.isPending}
            style={{ background: 'var(--color-teal,#00B097)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5 }}
          >
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
